import {
  type GSplatShaderEffectConfig,
  type GSplatShaderEffectScopeResolution,
  resolveGSplatShaderEffectScope,
  resolveGSplatShaderEffectSource,
} from '@/plugins/gsplat/effects/shaderEffectTypes'
import type { GSplatHandleBehavior } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatMaterialLeaf } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import {
  createGSplatModifyChunk,
  createGSplatModifyFragmentChunk,
  createGSplatModifyFragmentWGSLChunk,
  createGSplatModifyWGSLChunk,
} from '@/plugins/gsplat/shaders/gsplatModifyChunk'
import type { AssetId } from '@/shared/types/core'
import type { GSplatShaderEffectLike, LODStats } from '@/shared/types/gsplat'

interface MaterialValueBackup {
  readonly existed: boolean
  readonly value?: unknown
}

interface GSplatShaderEffectMaterialBridge {
  setShaderEffect(config: GSplatShaderEffectConfig | null): void
  clearShaderEffect(expected?: GSplatShaderEffectConfig): void
  deactivateShaderEffect(config: GSplatShaderEffectConfig): boolean
  setShaderEffectUniform(name: string, value: unknown): boolean
}

/**
 * Unified 共享 template material 上的公开 effect 行为适配。
 *
 * PlayCanvas Unified 的 material 是 system-wide template；每个 View 的 renderer 在 revision
 * 变化后复制 shader/define/parameter。这里不创建第二套 GSplat 数据或渲染器，只把既有
 * GSplatShaderEffectLike 生命周期翻译为当前 backend language 的 template material 变更。
 */
export class GSplatUnifiedShaderEffectController {
  private readonly parameterBackups = new Map<string, MaterialValueBackup>()
  private readonly defineBackups = new Map<string, MaterialValueBackup>()
  private readonly materialBridge: GSplatShaderEffectMaterialBridge
  private chunkBackup: MaterialValueBackup | null = null
  private fragmentChunkBackup: MaterialValueBackup | null = null
  private activeEffect: GSplatShaderEffectLike | null = null
  private activeConfig: GSplatShaderEffectConfig | null = null
  private activeOwner: AssetId | null = null
  private disposed = false

  get resolvedScope(): GSplatShaderEffectScopeResolution {
    return resolveGSplatShaderEffectScope(this.activeConfig?.scope ?? 'material', 'unified')
  }

  constructor(
    private readonly templateMaterial: GSplatMaterialLeaf,
    private readonly readLiveMaterials: () => readonly GSplatMaterialLeaf[],
    private readonly onVisualActivity: (reason: string, id?: AssetId) => void,
    private readonly onOwnerChanged: (owner: AssetId | null) => void = () => undefined
  ) {
    this.materialBridge = {
      setShaderEffect: (config) => this.applyConfig(config),
      clearShaderEffect: (expected) => this.clearConfig(expected),
      deactivateShaderEffect: (config) => this.deactivateConfig(config),
      setShaderEffectUniform: (name, value) => this.setMaterialUniform(name, value),
    }
  }

  createBehavior(owner: AssetId, getLodStats: () => LODStats): GSplatHandleBehavior {
    return {
      getLodStats,
      getShaderEffect: () => (this.activeOwner === owner ? this.activeEffect : null),
      setShaderEffect: (effect) => this.setShaderEffect(owner, effect),
      clearShaderEffect: () => this.clearShaderEffect(owner),
      restartShaderEffect: () => this.restartShaderEffect(owner),
      setShaderEffectUniform: (name, value) => this.setShaderEffectUniform(name, value, owner),
      dispose: () => this.releaseOwner(owner),
    }
  }

  getShaderEffect(): GSplatShaderEffectLike | null {
    this.assertActive()
    return this.activeEffect
  }

  setShaderEffect(owner: AssetId, effect: GSplatShaderEffectLike | null): boolean {
    this.assertActive()
    if (this.activeEffect === effect) {
      this.activeOwner = effect ? owner : null
      this.onOwnerChanged(this.activeOwner)
      return effect ? effect.enable(this.materialBridge) : true
    }

    this.activeEffect?.disable()
    this.activeEffect = effect
    this.activeOwner = effect ? owner : null
    this.onOwnerChanged(this.activeOwner)
    if (!effect) {
      this.clearConfig()
      return true
    }
    return effect.enable(this.materialBridge)
  }

  clearShaderEffect(owner?: AssetId): boolean {
    this.assertActive()
    if (owner !== undefined && this.activeOwner !== owner) return true
    const effect = this.activeEffect
    if (!effect) {
      this.clearConfig()
      return true
    }

    effect.disable()
    this.activeEffect = null
    this.activeOwner = null
    this.onOwnerChanged(null)
    this.clearConfig()
    return true
  }

  restartShaderEffect(owner?: AssetId): boolean {
    this.assertActive()
    if (owner !== undefined && this.activeOwner !== owner) return false
    return this.activeEffect?.restart() ?? false
  }

  setShaderEffectUniform(name: string, value: unknown, owner?: AssetId): boolean {
    this.assertActive()
    if (owner !== undefined && this.activeOwner !== owner) return false
    const changed = this.activeEffect?.setUniform?.(name, value)
    return changed ?? this.setMaterialUniform(name, value)
  }

  update(delta: number): void {
    this.assertActive()
    const effect = this.activeEffect
    if (!effect?.enabled) return
    effect.update(delta)
    if (effect.enabled) this.onVisualActivity('gsplat.shaderEffect', this.activeOwner ?? undefined)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    let firstError: unknown
    try {
      this.activeEffect?.destroy()
    } catch (error) {
      firstError = error
    }
    this.activeEffect = null
    this.activeOwner = null
    this.onOwnerChanged(null)
    try {
      this.restoreConfig()
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  private releaseOwner(owner: AssetId): void {
    if (this.disposed || this.activeOwner !== owner) return
    const effect = this.activeEffect
    this.activeEffect = null
    this.activeOwner = null
    this.onOwnerChanged(null)
    let firstError: unknown
    try {
      effect?.destroy()
    } catch (error) {
      firstError = error
    }
    try {
      this.restoreConfig()
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  private applyConfig(config: GSplatShaderEffectConfig | null): void {
    this.assertActive()
    if (!config) {
      this.restoreConfig()
      return
    }

    const language = this.templateMaterial.language
    const vertexSource = resolveGSplatShaderEffectSource(config, language, 'vertex')
    const fragmentSource = resolveGSplatShaderEffectSource(config, language, 'fragment')

    this.restoreConfig(false)
    this.activeConfig = config
    const chunks = this.templateMaterial.getShaderChunks(language)
    this.chunkBackup = chunks.has('gsplatModifyVS')
      ? { existed: true, value: chunks.get('gsplatModifyVS') }
      : { existed: false }
    this.fragmentChunkBackup = chunks.has('gsplatModifyPS')
      ? { existed: true, value: chunks.get('gsplatModifyPS') }
      : { existed: false }

    for (const [name, uniform] of Object.entries(config.uniforms ?? {})) {
      this.parameterBackups.set(name, this.captureParameter(name))
      this.templateMaterial.setParameter(name, uniform.value)
      this.syncLiveParameter(name, uniform.value)
    }
    for (const [name, value] of Object.entries(config.defines ?? {})) {
      this.defineBackups.set(name, this.captureDefine(name))
      this.templateMaterial.setDefine(name, value)
    }

    // WebGPU material parameters synchronously refresh the cached Quad pipeline. Publish the
    // modifier chunks only after every effect parameter/define is present; otherwise a Trees
    // transition briefly exposes a shader that references uniforms not yet bound.
    if (language === 'wgsl') {
      if (vertexSource) {
        chunks.set('gsplatModifyVS', createGSplatModifyWGSLChunk(vertexSource, config.defines))
      } else {
        chunks.delete('gsplatModifyVS')
      }
    } else {
      chunks.set('gsplatModifyVS', createGSplatModifyChunk(vertexSource))
    }
    if (language === 'wgsl') {
      if (fragmentSource) {
        chunks.set(
          'gsplatModifyPS',
          createGSplatModifyFragmentWGSLChunk(fragmentSource, config.defines)
        )
      } else {
        chunks.delete('gsplatModifyPS')
      }
    } else {
      chunks.set('gsplatModifyPS', createGSplatModifyFragmentChunk(fragmentSource))
    }

    this.templateMaterial.update()
    this.onVisualActivity('gsplat.shaderEffect', this.activeOwner ?? undefined)
  }

  private clearConfig(expected?: GSplatShaderEffectConfig): void {
    if (expected && this.activeConfig !== expected) return
    this.restoreConfig()
  }

  private deactivateConfig(config: GSplatShaderEffectConfig): boolean {
    if (this.activeConfig !== config) return false
    for (const [name, backup] of this.parameterBackups) {
      if (!backup.existed) continue
      this.templateMaterial.setParameter(name, backup.value)
      this.syncLiveParameter(name, backup.value)
    }
    this.onVisualActivity('gsplat.shaderEffect', this.activeOwner ?? undefined)
    return true
  }

  private setMaterialUniform(name: string, value: unknown): boolean {
    if (!this.activeConfig) return false
    const uniform = this.activeConfig.uniforms?.[name]
    if (uniform) uniform.value = value
    this.templateMaterial.setParameter(name, value)
    this.syncLiveParameter(name, value)
    this.onVisualActivity('gsplat.shaderEffectUniform', this.activeOwner ?? undefined)
    return true
  }

  private restoreConfig(update = true): void {
    const config = this.activeConfig
    if (!config && !this.chunkBackup) return
    this.activeConfig = null

    const chunks = this.templateMaterial.getShaderChunks(this.templateMaterial.language)
    // Clear the active modifier before restoring/deleting its parameters. WebGPU material
    // parameters synchronously rebuild the raster pipeline; restoring chunks first would
    // briefly leave (for example) Trees WGSL active without its required uniform values.
    chunks.delete('gsplatModifyVS')
    chunks.delete('gsplatModifyPS')

    for (const [name, backup] of this.parameterBackups) {
      if (backup.existed) {
        this.templateMaterial.setParameter(name, backup.value)
        this.syncLiveParameter(name, backup.value)
      } else {
        this.templateMaterial.deleteParameter(name)
      }
    }
    this.parameterBackups.clear()
    for (const [name, backup] of this.defineBackups) {
      this.templateMaterial.setDefine(name, backup.existed ? backup.value : undefined)
    }
    this.defineBackups.clear()

    if (this.chunkBackup?.existed) {
      chunks.set('gsplatModifyVS', this.chunkBackup.value as string)
    } else {
      chunks.delete('gsplatModifyVS')
    }
    this.chunkBackup = null
    if (this.fragmentChunkBackup?.existed) {
      chunks.set('gsplatModifyPS', this.fragmentChunkBackup.value as string)
    } else {
      chunks.delete('gsplatModifyPS')
    }
    this.fragmentChunkBackup = null

    let disposeError: unknown
    try {
      config?.dispose?.()
    } catch (error) {
      disposeError = error
    }
    if (update) {
      this.templateMaterial.update()
      this.onVisualActivity('gsplat.shaderEffect', this.activeOwner ?? undefined)
    }
    if (disposeError) throw disposeError
  }

  private captureParameter(name: string): MaterialValueBackup {
    const parameters = new Map(
      this.templateMaterial.captureSnapshot(this.templateMaterial.language).parameters
    )
    return parameters.has(name)
      ? { existed: true, value: parameters.get(name) }
      : { existed: false }
  }

  private captureDefine(name: string): MaterialValueBackup {
    const defines = new Map(
      this.templateMaterial.captureSnapshot(this.templateMaterial.language).defines
    )
    return defines.has(name) ? { existed: true, value: defines.get(name) } : { existed: false }
  }

  private syncLiveParameter(name: string, value: unknown): void {
    for (const material of this.readLiveMaterials()) {
      if (material !== this.templateMaterial) material.setParameter(name, value)
    }
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatUnifiedShaderEffectController 已销毁')
  }
}
